import { usePostFrontVerificaTipoVerifica } from '@framework/api-client/api/inquiries-compile/verifica-controller/verifica-controller.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { StyledHtmlText } from '@framework/engine/inputs/rich-text/style/StyledHtmlText.tsx';
import { LoginCard } from '@framework/ui/components/card/LoginCard.tsx';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { AlignRow } from '@framework/ui/components/row/AlignRow.tsx';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import { useHistory } from '@framework/ui/core/router/TypedRoute.tsx';
import { Button, Card, message, Result, Space } from 'antd';
import { type ResultStatusType } from 'antd/es/result';
import DOMPurify from 'dompurify';
import qs from 'qs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';
import { PageLayout } from '../../ui/layout/PageLayout.tsx';
import { getVerificationLabels } from './getVerificationLabels.ts';
import { getVerificationResultInfo } from './getVerificationResultInfo.ts';

const StyledVerificationCard = styled(Card)`
  width: 550px;
  ${down('md')} {
    min-width: 450px;
  }
  ${down('sm')} {
    min-width: unset;
    width: calc(100vw - 40px);
    > .ant-card-body {
      padding: 24px 12px;
    }
  }
`;

export interface IVerificationPageProps {}

/**
 * Componente di verifica.
 */
export function VerificationPage(props: IVerificationPageProps) {
  const { t } = useTranslation();
  useBreadcrumbItem({
    path: '/verifica',
    title: t('verification-page.verifica', 'Verifica'),
  });
  const history = useHistory();
  const verificationMutation = usePostFrontVerificaTipoVerifica();
  const [showResult, setShowResult] = useState(false);

  const queryParams = qs.parse(history.location.search, {
    ignoreQueryPrefix: true,
  });

  const { tipoVerifica, tipoverifica, ...allRequestParams } =
    queryParams as Record<string, string>;

  const handleVerification = async () => {
    try {
      await verificationMutation.mutateAsync({
        tipoVerifica: tipoVerifica ?? tipoverifica,
        // Non è corretta la specifica generata da Orval rispetto all'OpenAPI
        params: { ...allRequestParams } as any,
      });
      setShowResult(true);
    } catch (error) {
      console.error(
        t('verification-page.errore-in-verifica', 'Errore durante la verifica'),
        error,
      );
      // biome-ignore format: <tbd>
      message.error(getNetworkErrorMessage(error, t("verification-page.errore-durante-la-verifica", 'Si è verificato un problema durante la verifica')));
    }
  };

  const { status, message: verificationMessage } = getVerificationResultInfo(
    verificationMutation.data,
  );

  const { title, subTitle, buttonText } = getVerificationLabels(tipoVerifica);

  return (
    <PageLayout>
      <AlignRow justify="center">
        <LoaderSpin spinning={verificationMutation.isLoading} transparent>
          {!showResult ? (
            <LoginCard title={title} subTitle={subTitle}>
              <Space
                direction="vertical"
                size="large"
                style={{ width: '100%' }}
              >
                <Button
                  size="large"
                  type="primary"
                  block
                  onClick={handleVerification}
                >
                  {buttonText}
                </Button>
              </Space>
            </LoginCard>
          ) : (
            <StyledVerificationCard bordered={false}>
              <Result
                status={status as ResultStatusType}
                title={
                  <StyledHtmlText
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitize is safe
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(verificationMessage),
                    }}
                  />
                }
              />
            </StyledVerificationCard>
          )}
        </LoaderSpin>
      </AlignRow>
    </PageLayout>
  );
}
